import React, { useState, useRef, useEffect } from 'react';
import './TopicInput.css';

const TopicInput = ({ onGenerate, error }) => {
  const [topic, setTopic] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [showFirefoxMessage, setShowFirefoxMessage] = useState(false);
  const recognitionRef = useRef(null);
  const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

  useEffect(() => {
    // Initialize Web Speech API if available
    // Note: Firefox doesn't support SpeechRecognition API natively
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      try {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = false;
        recognitionRef.current.lang = 'en-US';

        recognitionRef.current.onresult = (event) => {
          const transcript = event.results[0][0].transcript;
          setTopic(transcript);
          setIsListening(false);
        };

        recognitionRef.current.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
        };

        recognitionRef.current.onend = () => {
          setIsListening(false);
        };
      } catch (err) {
        console.error('Failed to initialize speech recognition:', err);
        recognitionRef.current = null;
      }
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (err) {
          // Ignore errors when stopping
        }
      }
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (topic.trim()) {
      onGenerate(topic.trim());
    }
  };

  const handleVoiceInput = () => {
    // Show Firefox message if voice input is not supported
    if (isFirefox && !recognitionRef.current) {
      setShowFirefoxMessage(true);
      setTimeout(() => setShowFirefoxMessage(false), 5000);
      return;
    }

    if (recognitionRef.current) {
      try {
        if (isListening) {
          recognitionRef.current.stop();
          setIsListening(false);
        } else {
          recognitionRef.current.start();
          setIsListening(true);
        }
      } catch (err) {
        console.error('Speech recognition error:', err);
        setIsListening(false);
      }
    }
  };

  // Voice input is supported if recognition is available (Chrome, Edge, Safari)
  // Firefox doesn't support it natively, but we'll show the button with a message
  const isVoiceSupported = recognitionRef.current !== null || isFirefox;

  return (
    <div className="topic-input-container">
      <div className="topic-input-card">
        <h1 className="topic-input-title">AI Presentation Generator</h1>
        <p className="topic-input-subtitle">Enter a topic to generate a presentation</p>
        
        <form onSubmit={handleSubmit} className="topic-input-form">
          <div className="input-wrapper">
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Enter presentation topic..."
              className="topic-input"
              autoFocus
            />
            {isVoiceSupported && (
              <button
                type="button"
                onClick={handleVoiceInput}
                className={`voice-button ${isListening ? 'listening' : ''} ${isFirefox && !recognitionRef.current ? 'firefox-unsupported' : ''}`}
                title={isFirefox && !recognitionRef.current ? "Voice input not available in Firefox. Please type your topic." : "Voice input"}
              >
                🎤
              </button>
            )}
            {showFirefoxMessage && (
              <div className="firefox-message">
                Voice input is not available in Firefox. Please type your topic or use Chrome/Edge for voice input.
              </div>
            )}
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <button
            type="submit"
            disabled={!topic.trim()}
            className="generate-button"
          >
            Generate Presentation
          </button>
        </form>
      </div>
    </div>
  );
};

export default TopicInput;
